import {
	Directive,
	ElementRef,
	Inject,
	OnDestroy,
	OnInit,
	Renderer2,
	DOCUMENT,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectUser, State } from '@lmc-app/store';
import { filter, Subject, takeUntil } from 'rxjs';

import { User } from '@lmc-app/shared/interface/user.interface';

/**
 * Directive to control the visibility of the top navigation bar based on:
 * 1. Current route URL
 * 2. Demo mode query parameter
 * 3. User position/role
 *
 * This directive also manages body CSS classes for proper page layout.
 *
 * @example
 * <app-top-nav appNavVisibility></app-top-nav>
 */
@Directive({ selector: '[appNavVisibility]' })
export class NavVisibilityDirective implements OnInit, OnDestroy {
	private readonly _onDestroy = new Subject<void>();

	/**
	 * URLs where the navigation bar should be hidden
	 */
	private hideNavBarUrls = [
		'login',
		'/oldCalculator?',
		'/calculator?',
		'/transunion?',
		'/inverite?',
		'/underwriting?',
		'/note',
		'uwp-dashboard',
		'tv-intermedia-calls',
		'tv-collection-reports',
		'tv-funded-reports',
		'documents',
		'preapproval',
		'transunion-consent-form',
	];

	/**
	 * URLs where body padding should be ignored (full-width pages)
	 */
	private ignoreBodyPaddingUrls = [
		'reports',
		'summary',
		'loan-meta',
		'admin-settings',
	];

	private userPosition: string;

	constructor(
		private el: ElementRef,
		private renderer: Renderer2,
		private router: Router,
		private store: Store<State>,
		@Inject(DOCUMENT) private document: Document
	) {}

	ngOnInit(): void {
		// Subscribe to user changes to adjust visibility rules based on user position
		this.store
			.select(selectUser)
			.pipe(takeUntil(this._onDestroy))
			.subscribe((user: User | null) => {
				if (user) {
					this.userPosition = user.position;

					// UW and FO users should see the nav bar on /note pages
					if (['UW', 'FO'].includes(this.userPosition)) {
						const notesIndex = this.hideNavBarUrls.indexOf('/note');
						if (notesIndex > -1) {
							this.hideNavBarUrls.splice(notesIndex, 1);
						}
					}

					this.updateVisibility();
				}
			});

		// Listen to route changes
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this._onDestroy)
			)
			.subscribe(() => {
				this.updateVisibility();
			});

		// Initial visibility check
		this.updateVisibility();
	}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	/**
	 * Updates the visibility of the navigation bar and body CSS classes
	 * based on current route and user context
	 */
	private updateVisibility(): void {
		const currentUrl = this.router.url;

		// Check if demo mode is enabled via query parameter
		const isDemoMode = this.checkDemoMode(currentUrl);

		// Check if current URL matches any hide rules
		const shouldHide =
			isDemoMode ||
			this.hideNavBarUrls.some((navUrl) => currentUrl.includes(navUrl));

		// Show or hide the navigation bar
		if (shouldHide) {
			this.renderer.setStyle(this.el.nativeElement, 'display', 'none');
			this.renderer.removeClass(this.document.body, 'has-nav-bar');
		} else {
			this.renderer.removeStyle(this.el.nativeElement, 'display');
			this.renderer.addClass(this.document.body, 'has-nav-bar');
		}

		// Handle body padding for full-width pages
		const shouldIgnorePadding = this.ignoreBodyPaddingUrls.some((navUrl) =>
			currentUrl.includes(navUrl)
		);

		if (shouldIgnorePadding) {
			this.renderer.addClass(this.document.body, 'ignore-body-padding');
		} else {
			this.renderer.removeClass(this.document.body, 'ignore-body-padding');
		}
	}

	/**
	 * Checks if demo mode is enabled in the current URL
	 */
	private checkDemoMode(url: string): boolean {
		// Simple check for demo query parameter
		if (url.includes('demo=true')) {
			return true;
		}

		// More robust check using URLSearchParams
		const queryStringIndex = url.indexOf('?');
		if (queryStringIndex > -1) {
			const queryString = url.substring(queryStringIndex + 1);
			const params = new URLSearchParams(queryString);
			return params.get('demo') === 'true';
		}

		return false;
	}
}
